import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../styles/MetricsScreen.scss";
import { apiFetch } from "../utils/api";
import { getAuthHeaders } from "../utils/auth";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";
const numberFormatter = new Intl.NumberFormat("ru-RU");
const STATUS_COLORS = ["#00DCE6", "#1C7BF7", "#FFB454", "#FF8A8A", "#6EE7B7"];
const TASK_ACTION_COLORS = [
  "#22d3ee",
  "#60a5fa",
  "#34d399",
  "#fbbf24",
  "#f97316",
  "#a78bfa",
];
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const EXCLUDED_HTTP_PATHS = new Set(["/metrics", "/metrics/history"]);

const sumByName = (series, metricName) =>
  series
    .filter((item) => item.name === metricName)
    .reduce((total, item) => total + item.value, 0);

const formatNumber = (value) => numberFormatter.format(value || 0);

const formatDuration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return "0 мс";
  }
  const ms = seconds * 1000;
  if (ms < 1000) {
    return `${Math.round(ms)} мс`;
  }
  if (seconds < 60) {
    return `${seconds.toFixed(2)} с`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes} мин ${remainder.toFixed(1)} с`;
};

const formatPercent = (value) => {
  if (!Number.isFinite(value) || value <= 0) {
    return "0%";
  }
  return `${value.toFixed(1)}%`;
};

const formatBytes = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 Б";
  }
  const units = ["Б", "КБ", "МБ", "ГБ", "ТБ"];
  let current = bytes;
  let unitIndex = 0;
  while (current >= 1024 && unitIndex < units.length - 1) {
    current /= 1024;
    unitIndex += 1;
  }
  const digits = current >= 100 ? 0 : current >= 10 ? 1 : 2;
  return `${current.toFixed(digits)} ${units[unitIndex]}`;
};

const formatActionLabel = (action) => {
  const labels = {
    page_visit: "Просмотр задач",
    completion_submitted: "Отправка отчета",
    created: "Создание задач",
    updated: "Обновление задач",
    deleted: "Удаление задач",
    sent_for_review: "Отправка на проверку",
    review_approved: "Ревью принято",
    review_rejected: "Ревью отклонено",
    completed_direct: "Прямое завершение",
  };

  if (!action) return "Другое";
  return labels[action] || action.replace(/_/g, " ");
};

const formatSecondsShort = (seconds) => {
  if (!Number.isFinite(seconds)) {
    return "∞";
  }
  if (seconds <= 0) {
    return "0";
  }
  const ms = seconds * 1000;
  if (ms < 1000) {
    return `${Math.round(ms)}мс`;
  }
  if (seconds < 10) {
    return `${seconds.toFixed(1)}с`;
  }
  if (seconds < 60) {
    return `${seconds.toFixed(0)}с`;
  }
  return `${(seconds / 60).toFixed(1)}м`;
};

const buildConicGradient = (segments) => {
  if (!segments.length) {
    return "conic-gradient(rgba(255,255,255,0.06) 0deg, rgba(255,255,255,0.02) 360deg)";
  }
  let start = 0;
  const stops = segments.map((segment) => {
    const end = start + segment.percent;
    const stop = `${segment.color} ${start}% ${end}%`;
    start = end;
    return stop;
  });
  return `conic-gradient(${stops.join(", ")})`;
};

const isExcludedHTTPPath = (path) => EXCLUDED_HTTP_PATHS.has(path || "");
const isValidISODate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || "");

const getTodayISODate = () => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 10);
};

const formatDateLabel = (value) => {
  if (!isValidISODate(value)) {
    return value || "Не выбрано";
  }
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDateShortLabel = (value) => {
  if (!isValidISODate(value)) {
    return value || "Дата";
  }
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "short",
  });
};

const formatDateTimeLabel = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatVPNEventLabel = (eventType) => {
  const labels = {
    temp_block: "Временная блокировка",
    temp_unblock: "Авто-разблокировка",
    manual_disable: "Ручное отключение",
    manual_enable: "Ручное включение",
  };
  return labels[eventType] || eventType || "Событие";
};

export default function MetricsScreen() {
  const [series, setSeries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedDate, setSelectedDate] = useState(getTodayISODate());
  const [availableDates, setAvailableDates] = useState([]);
  const [vpnAdmin, setVpnAdmin] = useState(null);
  const [vpnAdminError, setVpnAdminError] = useState("");
  const isMounted = useRef(true);
  const lastHashRef = useRef("");
  const activeRequestRef = useRef(0);
  const todayDate = getTodayISODate();

  const loadMetrics = useCallback(
    async (targetDate = selectedDate) => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }

      const requestId = activeRequestRef.current + 1;
      activeRequestRef.current = requestId;
      setIsLoading(true);
      setError("");

      try {
        const query = isValidISODate(targetDate)
          ? `?date=${encodeURIComponent(targetDate)}`
          : "";
        const response = await apiFetch(`${API_BASE}/metrics/history${query}`, {
          cache: "no-store",
          headers: {
            ...getAuthHeaders(),
          },
        });
        if (!response.ok) throw new Error();

        const payload = await response.json();
        if (!isMounted.current || requestId !== activeRequestRef.current)
          return;

        const nextSeries = Array.isArray(payload?.series)
          ? payload.series.filter(
              (item) =>
                item &&
                typeof item === "object" &&
                typeof item.name === "string" &&
                typeof item.value === "number" &&
                item.labels &&
                typeof item.labels === "object",
            )
          : [];
        const nextAvailableDates = Array.isArray(payload?.available_dates)
          ? payload.available_dates.filter((date) => isValidISODate(date))
          : [];
        const normalizedAvailableDates = [...new Set(nextAvailableDates)].sort(
          (a, b) => b.localeCompare(a),
        );
        const normalizedDate = isValidISODate(payload?.date)
          ? payload.date
          : targetDate;
        const nextVpnAdmin =
          payload?.vpn_admin && typeof payload.vpn_admin === "object"
            ? payload.vpn_admin
            : null;
        const nextVpnAdminError =
          typeof payload?.vpn_admin_error === "string"
            ? payload.vpn_admin_error
            : "";
        const hash = JSON.stringify({
          date: normalizedDate,
          series: nextSeries,
          vpn_admin: nextVpnAdmin,
          vpn_admin_error: nextVpnAdminError,
        });
        setAvailableDates(normalizedAvailableDates);
        setVpnAdmin(nextVpnAdmin);
        setVpnAdminError(nextVpnAdminError);
        if (isValidISODate(normalizedDate) && normalizedDate !== selectedDate) {
          setSelectedDate(normalizedDate);
        }
        if (hash === lastHashRef.current) {
          setLastUpdated(new Date());
          return;
        }

        lastHashRef.current = hash;
        setSeries(nextSeries);
        setLastUpdated(new Date());
      } catch {
        if (isMounted.current && requestId === activeRequestRef.current) {
          setError("Не удалось загрузить метрики за выбранный день");
        }
      } finally {
        if (isMounted.current && requestId === activeRequestRef.current) {
          setIsLoading(false);
        }
      }
    },
    [selectedDate],
  );

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadMetrics(selectedDate);
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [loadMetrics, selectedDate]);

  useEffect(() => {
    isMounted.current = true;
    loadMetrics(selectedDate);
    const interval = setInterval(
      () => loadMetrics(selectedDate),
      REFRESH_INTERVAL_MS,
    );
    return () => {
      isMounted.current = false;
      clearInterval(interval);
    };
  }, [loadMetrics, selectedDate]);

  const dateOptions = useMemo(() => {
    const dateSet = new Set(availableDates);
    if (isValidISODate(selectedDate)) {
      dateSet.add(selectedDate);
    }
    if (isValidISODate(todayDate)) {
      dateSet.add(todayDate);
    }
    return [...dateSet].sort((a, b) => b.localeCompare(a));
  }, [availableDates, selectedDate, todayDate]);
  const selectedDateIndex = dateOptions.findIndex(
    (date) => date === selectedDate,
  );
  const newerDate =
    selectedDateIndex > 0 ? dateOptions[selectedDateIndex - 1] : "";
  const olderDate =
    selectedDateIndex >= 0 && selectedDateIndex < dateOptions.length - 1
      ? dateOptions[selectedDateIndex + 1]
      : "";
  const visibleDateOptions = useMemo(() => {
    if (!dateOptions.length) {
      return [];
    }
    const safeIndex = selectedDateIndex >= 0 ? selectedDateIndex : 0;
    const start = Math.max(0, safeIndex - 2);
    const end = Math.min(dateOptions.length, start + 5);
    const normalizedStart = Math.max(0, end - 5);
    return dateOptions.slice(normalizedStart, end);
  }, [dateOptions, selectedDateIndex]);

  const summary = useMemo(() => {
    const httpRequests = series.filter(
      (item) =>
        item.name === "http_requests_total" &&
        !isExcludedHTTPPath(item.labels.path || ""),
    );
    const durationSum = series
      .filter(
        (item) =>
          item.name === "http_request_duration_seconds_sum" &&
          !isExcludedHTTPPath(item.labels.path || ""),
      )
      .reduce((total, item) => total + item.value, 0);
    const durationCount = series
      .filter(
        (item) =>
          item.name === "http_request_duration_seconds_count" &&
          !isExcludedHTTPPath(item.labels.path || ""),
      )
      .reduce((total, item) => total + item.value, 0);
    const totalRequests = httpRequests.reduce(
      (total, item) => total + item.value,
      0,
    );
    const avgDuration = durationCount > 0 ? durationSum / durationCount : 0;
    const taskVisits = sumByName(series, "task_page_visits_total");
    const taskCompletions = sumByName(series, "task_completions_total");

    const completionUsers = series.filter(
      (item) => item.name === "task_completion_users" && item.value > 0,
    );

    const requestByPath = {};
    const requestByStatus = {};
    httpRequests.forEach((item) => {
      const path = item.labels.path || "unknown";
      const status = item.labels.status || "unknown";
      requestByPath[path] = (requestByPath[path] || 0) + item.value;
      requestByStatus[status] = (requestByStatus[status] || 0) + item.value;
    });

    const topEndpoints = Object.entries(requestByPath)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([path, value]) => ({ path, value }));

    const statusBreakdown = Object.entries(requestByStatus)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([status, value]) => ({ status, value }));

    const taskVisitsByPage = series
      .filter((item) => item.name === "task_page_visits_total")
      .map((item) => ({
        page: item.labels.page || "unknown",
        value: item.value,
      }))
      .sort((a, b) => b.value - a.value);

    const taskActions = {};
    series
      .filter((item) => item.name === "task_actions_total")
      .forEach((item) => {
        const action = item.labels.action || "unknown";
        taskActions[action] = (taskActions[action] || 0) + item.value;
      });

    const reviews = {};
    series
      .filter((item) => item.name === "task_reviews_total")
      .forEach((item) => {
        const decision = item.labels.decision || "unknown";
        reviews[decision] = (reviews[decision] || 0) + item.value;
      });

    const reviewApproved = reviews.approved || 0;
    const reviewRejected = reviews.rejected || 0;
    const reviewTotal = reviewApproved + reviewRejected;
    const reviewApprovalRate =
      reviewTotal > 0 ? (reviewApproved / reviewTotal) * 100 : 0;
    const taskActionsBreakdown = Object.entries(taskActions)
      .sort((a, b) => b[1] - a[1])
      .map(([action, value], index) => ({
        action,
        value,
        color: TASK_ACTION_COLORS[index % TASK_ACTION_COLORS.length],
      }));

    const taskStatusTransitions = series
      .filter((item) => item.name === "task_status_transitions_total")
      .map((item) => ({
        from: item.labels.from_status || "unknown",
        to: item.labels.to_status || "unknown",
        value: item.value,
      }))
      .sort((a, b) => b.value - a.value);

    return {
      totalRequests,
      avgDuration,
      taskVisits,
      taskCompletions,
      completionUsers: completionUsers.length,
      uniquePaths: Object.keys(requestByPath).length,
      topEndpoints,
      statusBreakdown,
      taskVisitsByPage,
      taskCreated: taskActions.created || 0,
      taskUpdated: taskActions.updated || 0,
      taskDeleted: taskActions.deleted || 0,
      taskSentForReview: taskActions.sent_for_review || 0,
      taskCompletedDirect: taskActions.completed_direct || 0,
      reviewApproved,
      reviewRejected,
      reviewTotal,
      reviewApprovalRate,
      taskActionsBreakdown,
      taskStatusTransitions,
    };
  }, [series]);

  const latencyBuckets = useMemo(() => {
    const bucketTotals = {};
    series
      .filter(
        (item) =>
          item.name === "http_request_duration_seconds_bucket" &&
          !isExcludedHTTPPath(item.labels.path || ""),
      )
      .forEach((item) => {
        const le = item.labels.le || "+Inf";
        bucketTotals[le] = (bucketTotals[le] || 0) + item.value;
      });

    const entries = Object.entries(bucketTotals)
      .map(([le, value]) => ({
        le,
        value,
        leNum: le === "+Inf" ? Number.POSITIVE_INFINITY : Number(le),
      }))
      .filter((entry) => !Number.isNaN(entry.leNum))
      .sort((a, b) => a.leNum - b.leNum);

    let prevValue = 0;
    let prevLe = 0;
    const buckets = entries.map((entry) => {
      const count = Math.max(0, entry.value - prevValue);
      const label =
        entry.leNum === Number.POSITIVE_INFINITY
          ? `> ${formatSecondsShort(prevLe)}`
          : `${formatSecondsShort(prevLe)}–${formatSecondsShort(entry.leNum)}`;
      prevValue = entry.value;
      if (Number.isFinite(entry.leNum)) {
        prevLe = entry.leNum;
      }
      return { label, count };
    });

    return buckets.filter((bucket) => bucket.count > 0).slice(0, 8);
  }, [series]);

  const maxEndpoint = Math.max(
    1,
    ...summary.topEndpoints.map((item) => item.value),
  );
  const maxTaskPage = Math.max(
    1,
    ...summary.taskVisitsByPage.map((item) => item.value),
  );
  const transitionBars = summary.taskStatusTransitions.slice(0, 8);
  const maxTransition = Math.max(
    1,
    ...transitionBars.map((item) => item.value),
  );
  const maxLatency = Math.max(1, ...latencyBuckets.map((item) => item.count));

  const statusTotal = summary.statusBreakdown.reduce(
    (total, item) => total + item.value,
    0,
  );
  const statusSegments = statusTotal
    ? summary.statusBreakdown.map((item, index) => ({
        label: item.status,
        value: item.value,
        percent: (item.value / statusTotal) * 100,
        color: STATUS_COLORS[index % STATUS_COLORS.length],
      }))
    : [];
  const statusGradient = buildConicGradient(statusSegments);
  const taskPageBars = summary.taskVisitsByPage.slice(0, 6);

  const actionTotal = summary.taskActionsBreakdown.reduce(
    (total, item) => total + item.value,
    0,
  );
  const actionSegments = actionTotal
    ? summary.taskActionsBreakdown.slice(0, 6).map((item) => ({
        label: formatActionLabel(item.action),
        value: item.value,
        percent: (item.value / actionTotal) * 100,
        color: item.color,
      }))
    : [];
  const actionGradient = buildConicGradient(actionSegments);

  const reviewSegments = summary.reviewTotal
    ? [
        {
          label: "Принято",
          value: summary.reviewApproved,
          percent: (summary.reviewApproved / summary.reviewTotal) * 100,
          color: "#34d399",
        },
        {
          label: "Отклонено",
          value: summary.reviewRejected,
          percent: (summary.reviewRejected / summary.reviewTotal) * 100,
          color: "#fb7185",
        },
      ]
    : [];
  const reviewGradient = buildConicGradient(reviewSegments);

  const lifecycleStages = [
    { key: "created", label: "Создано", value: summary.taskCreated },
    {
      key: "sent_for_review",
      label: "На проверке",
      value: summary.taskSentForReview,
    },
    { key: "approved", label: "Принято", value: summary.reviewApproved },
    { key: "rejected", label: "Отклонено", value: summary.reviewRejected },
    {
      key: "direct",
      label: "Прямое завершение",
      value: summary.taskCompletedDirect,
    },
    { key: "deleted", label: "Удалено", value: summary.taskDeleted },
  ];
  const lifecycleMax = Math.max(
    1,
    ...lifecycleStages.map((item) => item.value),
  );

  const vpnTotals = vpnAdmin?.totals || {};
  const vpnEvents24h = vpnAdmin?.events_24h || {};
  const vpnTopTraffic = Array.isArray(vpnAdmin?.top_users_by_traffic)
    ? vpnAdmin.top_users_by_traffic
    : [];
  const vpnTopDevices = Array.isArray(vpnAdmin?.top_users_by_devices)
    ? vpnAdmin.top_users_by_devices
    : [];
  const vpnRecentEvents = Array.isArray(vpnAdmin?.recent_events)
    ? vpnAdmin.recent_events
    : [];
  const vpnTrafficMax = Math.max(
    1,
    ...vpnTopTraffic.map((item) => Number(item?.used_traffic_bytes || 0)),
  );
  return (
    <section className="metrics-screen">
      <div className="metrics-screen__glow" />
      <header className="metrics-screen__hero">
        <div className="metrics-screen__title-block">
          <h2 className="metrics-screen__title">Метрики</h2>
          <p className="metrics-screen__subtitle">
            Живые показатели платформы по дням: трафик API, активность задач и
            стабильность.
          </p>
        </div>
        <div className="metrics-screen__hero-actions">
          <div className="metrics-screen__date-filter">
            <span className="metrics-screen__date-label">День</span>
            <div className="metrics-screen__date-line">
              <button
                className="metrics-screen__date-arrow"
                type="button"
                onClick={() => newerDate && setSelectedDate(newerDate)}
                disabled={!newerDate || isLoading}
                aria-label="Более новый день"
                title="Более новый день"
              >
                {"<"}
              </button>
              <div className="metrics-screen__date-track">
                {visibleDateOptions.map((date) => (
                  <button
                    key={date}
                    className={`metrics-screen__date-item${
                      date === selectedDate ? " is-active" : ""
                    }`}
                    type="button"
                    onClick={() => setSelectedDate(date)}
                    disabled={isLoading && date === selectedDate}
                  >
                    {formatDateShortLabel(date)}
                  </button>
                ))}
              </div>
              <button
                className="metrics-screen__date-arrow"
                type="button"
                onClick={() => olderDate && setSelectedDate(olderDate)}
                disabled={!olderDate || isLoading}
                aria-label="Более старый день"
                title="Более старый день"
              >
                {">"}
              </button>
            </div>
            <button
              className="metrics-screen__date-today"
              type="button"
              onClick={() => setSelectedDate(todayDate)}
              disabled={selectedDate === todayDate}
            >
              Сегодня
            </button>
          </div>
          <div className="metrics-screen__updated">
            {`День: ${formatDateLabel(selectedDate)}`}
            {lastUpdated
              ? ` · Обновлено ${lastUpdated.toLocaleTimeString("ru-RU", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`
              : " · Ожидание данных"}
          </div>
          <button
            className="metrics-screen__refresh"
            type="button"
            onClick={() => !isLoading && loadMetrics(selectedDate)}
            disabled={isLoading}
          >
            {isLoading ? "Обновление..." : "Обновить"}
          </button>
        </div>
      </header>

      {error && (
        <div className="metrics-screen__state metrics-screen__state--error">
          {error}
        </div>
      )}
      {!error && isLoading && series.length === 0 && (
        <div className="metrics-screen__state">Загрузка метрик...</div>
      )}

      <div className="metrics-screen__grid">
        <article className="metrics-card">
          <span className="metrics-card__label">API-запросы</span>
          <span className="metrics-card__value">
            {formatNumber(summary.totalRequests)}
          </span>
          <span className="metrics-card__hint">общее количество запросов</span>
        </article>
        <article className="metrics-card">
          <span className="metrics-card__label">Среднее время API</span>
          <span className="metrics-card__value">
            {formatDuration(summary.avgDuration)}
          </span>
          <span className="metrics-card__hint">без scrape-запросов</span>
        </article>
        <article className="metrics-card">
          <span className="metrics-card__label">Посещения задач</span>
          <span className="metrics-card__value">
            {formatNumber(summary.taskVisits)}
          </span>
          <span className="metrics-card__hint">страницы задач</span>
        </article>
        <article className="metrics-card">
          <span className="metrics-card__label">Отчеты о выполнении</span>
          <span className="metrics-card__value">
            {formatNumber(summary.taskCompletions)}
          </span>
          <span className="metrics-card__hint">отправлено отчетов</span>
        </article>
        <article className="metrics-card">
          <span className="metrics-card__label">Исполнители с отчетами</span>
          <span className="metrics-card__value">
            {formatNumber(summary.completionUsers)}
          </span>
          <span className="metrics-card__hint">уникальные пользователи</span>
        </article>
        <article className="metrics-card">
          <span className="metrics-card__label">Создано задач</span>
          <span className="metrics-card__value">
            {formatNumber(summary.taskCreated)}
          </span>
          <span className="metrics-card__hint">с запуска сервиса</span>
        </article>
        <article className="metrics-card">
          <span className="metrics-card__label">Обновлено задач</span>
          <span className="metrics-card__value">
            {formatNumber(summary.taskUpdated)}
          </span>
          <span className="metrics-card__hint">любые изменения</span>
        </article>
        <article className="metrics-card">
          <span className="metrics-card__label">Удалено задач</span>
          <span className="metrics-card__value">
            {formatNumber(summary.taskDeleted)}
          </span>
          <span className="metrics-card__hint">с подтвержденным удалением</span>
        </article>
        <article className="metrics-card">
          <span className="metrics-card__label">На проверку</span>
          <span className="metrics-card__value">
            {formatNumber(summary.taskSentForReview)}
          </span>
          <span className="metrics-card__hint">отправлено исполнителями</span>
        </article>
        <article className="metrics-card">
          <span className="metrics-card__label">Ревью приняты</span>
          <span className="metrics-card__value">
            {formatNumber(summary.reviewApproved)}
          </span>
          <span className="metrics-card__hint">решения approve</span>
        </article>
        <article className="metrics-card">
          <span className="metrics-card__label">Ревью отклонены</span>
          <span className="metrics-card__value">
            {formatNumber(summary.reviewRejected)}
          </span>
          <span className="metrics-card__hint">решения reject</span>
        </article>
        <article className="metrics-card">
          <span className="metrics-card__label">Успешность ревью</span>
          <span className="metrics-card__value">
            {formatPercent(summary.reviewApprovalRate)}
          </span>
          <span className="metrics-card__hint">доля принятых отчетов</span>
        </article>
        <article className="metrics-card">
          <span className="metrics-card__label">Прямое завершение</span>
          <span className="metrics-card__value">
            {formatNumber(summary.taskCompletedDirect)}
          </span>
          <span className="metrics-card__hint">через смену статуса</span>
        </article>
        <article className="metrics-card">
          <span className="metrics-card__label">Уникальные эндпоинты</span>
          <span className="metrics-card__value">
            {formatNumber(summary.uniquePaths)}
          </span>
          <span className="metrics-card__hint">в трафике API</span>
        </article>
      </div>

      <div className="metrics-screen__visuals">
        <section className="metrics-visual">
          <div className="metrics-visual__header">
            <h3>Статусы ответов</h3>
            <span>Топ 4 статуса</span>
          </div>
          <div className="metrics-visual__body">
            <div
              className="metrics-donut"
              style={{ background: statusGradient }}
            >
              <span className="metrics-donut__value">
                {formatNumber(statusTotal)}
              </span>
              <span className="metrics-donut__label">запросов</span>
            </div>
            <div className="metrics-legend">
              {statusSegments.length === 0 && (
                <span className="metrics-legend__empty">Нет данных</span>
              )}
              {statusSegments.map((segment) => (
                <div className="metrics-legend__item" key={segment.label}>
                  <span
                    className="metrics-legend__dot"
                    style={{ backgroundColor: segment.color }}
                  />
                  <span className="metrics-legend__text">{segment.label}</span>
                  <span className="metrics-legend__value">
                    {formatNumber(segment.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="metrics-visual">
          <div className="metrics-visual__header">
            <h3>Распределение задержек</h3>
            <span>Насколько быстро работает система</span>
          </div>
          <div className="metrics-visual__body">
            {latencyBuckets.length === 0 ? (
              <p className="metrics-visual__empty">Нет данных по задержкам</p>
            ) : (
              <div className="metrics-histogram">
                {latencyBuckets.map((bucket) => (
                  <div className="metrics-histogram__item" key={bucket.label}>
                    <div
                      className="metrics-histogram__bar"
                      style={{
                        height: `${(bucket.count / maxLatency) * 100}%`,
                      }}
                      title={`${bucket.label}: ${formatNumber(bucket.count)}`}
                    />
                    <span className="metrics-histogram__label">
                      {bucket.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="metrics-visual metrics-visual--accent">
          <div className="metrics-visual__header">
            <h3>Посещения задач</h3>
            <span>Где чаще всего работают</span>
          </div>
          <div className="metrics-visual__body">
            {taskPageBars.length === 0 ? (
              <p className="metrics-visual__empty">
                Нет данных по страницам задач
              </p>
            ) : (
              <div className="metrics-bar-chart">
                {taskPageBars.map((item) => (
                  <div className="metrics-bar-chart__item" key={item.page}>
                    <div
                      className="metrics-bar-chart__bar"
                      style={{ height: `${(item.value / maxTaskPage) * 100}%` }}
                      title={`${item.page}: ${formatNumber(item.value)}`}
                    />
                    <span className="metrics-bar-chart__label">
                      {item.page}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="metrics-visual">
          <div className="metrics-visual__header">
            <h3>Распределение действий</h3>
            <span>Какие task-события происходят чаще</span>
          </div>
          <div className="metrics-visual__body">
            {actionSegments.length === 0 ? (
              <p className="metrics-visual__empty">Нет данных по действиям</p>
            ) : (
              <>
                <div
                  className="metrics-donut metrics-donut--sm"
                  style={{ background: actionGradient }}
                >
                  <span className="metrics-donut__value">
                    {formatNumber(actionTotal)}
                  </span>
                  <span className="metrics-donut__label">действий</span>
                </div>
                <div className="metrics-legend">
                  {actionSegments.map((segment) => (
                    <div className="metrics-legend__item" key={segment.label}>
                      <span
                        className="metrics-legend__dot"
                        style={{ backgroundColor: segment.color }}
                      />
                      <span className="metrics-legend__text">
                        {segment.label}
                      </span>
                      <span className="metrics-legend__value">
                        {formatNumber(segment.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        <section className="metrics-visual metrics-visual--accent">
          <div className="metrics-visual__header">
            <h3>Баланс ревью</h3>
            <span>Approve vs Reject по задачам</span>
          </div>
          <div className="metrics-visual__body metrics-visual__body--column">
            {summary.reviewTotal === 0 ? (
              <p className="metrics-visual__empty">Нет данных по ревью</p>
            ) : (
              <>
                <div className="metrics-review">
                  <div
                    className="metrics-donut metrics-donut--sm"
                    style={{ background: reviewGradient }}
                  >
                    <span className="metrics-donut__value">
                      {formatPercent(summary.reviewApprovalRate)}
                    </span>
                    <span className="metrics-donut__label">approve rate</span>
                  </div>
                  <div className="metrics-legend">
                    {reviewSegments.map((segment) => (
                      <div className="metrics-legend__item" key={segment.label}>
                        <span
                          className="metrics-legend__dot"
                          style={{ backgroundColor: segment.color }}
                        />
                        <span className="metrics-legend__text">
                          {segment.label}
                        </span>
                        <span className="metrics-legend__value">
                          {formatNumber(segment.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="metrics-stack" title="Структура решений ревью">
                  {reviewSegments.map((segment) => (
                    <span
                      key={segment.label}
                      className="metrics-stack__segment"
                      style={{
                        width: `${segment.percent}%`,
                        backgroundColor: segment.color,
                      }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        <section className="metrics-visual">
          <div className="metrics-visual__header">
            <h3>Воронка задач</h3>
            <span>Путь от создания до финального решения</span>
          </div>
          <div className="metrics-visual__body metrics-visual__body--column">
            {lifecycleStages.every((stage) => stage.value === 0) ? (
              <p className="metrics-visual__empty">
                Нет данных по воронке задач
              </p>
            ) : (
              <div className="metrics-funnel">
                {lifecycleStages.map((stage) => (
                  <div className="metrics-funnel__item" key={stage.key}>
                    <div className="metrics-funnel__row">
                      <span>{stage.label}</span>
                      <strong>{formatNumber(stage.value)}</strong>
                    </div>
                    <div className="metrics-funnel__track">
                      <span
                        className="metrics-funnel__bar"
                        style={{
                          width: `${(stage.value / lifecycleMax) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="metrics-screen__panels">
        <section className="metrics-panel metrics-panel--accent">
          <div className="metrics-panel__header">
            <h2>ITC VPN (админ)</h2>
            <span>
              {vpnAdmin?.generated_at
                ? `snapshot · ${formatDateTimeLabel(vpnAdmin.generated_at)}`
                : "данные прокси-сервиса"}
            </span>
          </div>
          <div className="metrics-panel__body">
            {vpnAdminError && !vpnAdmin ? (
              <p className="metrics-panel__empty">
                VPN метрики недоступны: {vpnAdminError}
              </p>
            ) : !vpnAdmin ? (
              <p className="metrics-panel__empty">Нет данных по ITC VPN</p>
            ) : (
              <div className="metrics-list">
                {[
                  ["Аккаунтов VPN", formatNumber(vpnTotals.accounts_total)],
                  ["Эффективно активны", formatNumber(vpnTotals.effective_active_count)],
                  [
                    `Онлайн (<=${vpnAdmin?.windows?.online_window_seconds || 300}с)`,
                    formatNumber(vpnTotals.online_users_count),
                  ],
                  ["Временный блок (сейчас)", formatNumber(vpnTotals.temp_blocked_count)],
                  ["Ручной disable", formatNumber(vpnTotals.manual_disabled_count)],
                  ["Активных IP сейчас", formatNumber(vpnTotals.active_device_ips_count)],
                  [
                    "Пользователи с активными IP",
                    formatNumber(vpnTotals.users_with_active_devices_count),
                  ],
                  ["Трафик (текущий used)", formatBytes(vpnTotals.traffic_used_bytes)],
                  [
                    "Трафик (lifetime)",
                    formatBytes(vpnTotals.traffic_lifetime_used_bytes),
                  ],
                ].map(([label, value]) => (
                  <div className="metrics-list__item" key={label}>
                    <div className="metrics-list__row">
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="metrics-panel__footer">
            <span className="metrics-chip">
              Блокировок 24ч: {formatNumber(vpnEvents24h.temp_blocks)}
            </span>
            <span className="metrics-chip">
              Разблокировок 24ч: {formatNumber(vpnEvents24h.temp_unblocks)}
            </span>
            <span className="metrics-chip">
              Manual disable 24ч: {formatNumber(vpnEvents24h.manual_disables)}
            </span>
            <span className="metrics-chip">
              Manual enable 24ч: {formatNumber(vpnEvents24h.manual_enables)}
            </span>
            {vpnAdminError && vpnAdmin ? (
              <span className="metrics-chip">Предупреждение: {vpnAdminError}</span>
            ) : null}
          </div>
        </section>

        <section className="metrics-panel">
          <div className="metrics-panel__header">
            <h2>ITC VPN: топ по трафику</h2>
            <span>Текущий used traffic по Marzban</span>
          </div>
          <div className="metrics-panel__body">
            {vpnTopTraffic.length === 0 ? (
              <p className="metrics-panel__empty">Нет данных по VPN-трафику</p>
            ) : (
              <div className="metrics-list">
                {vpnTopTraffic.slice(0, 8).map((item) => {
                  const value = Number(item?.used_traffic_bytes || 0);
                  const label =
                    item?.display_name || item?.marzban_username || item?.telegram_id || "user";
                  return (
                    <div
                      className="metrics-list__item"
                      key={`vpn-traffic-${item.telegram_id || label}`}
                    >
                      <div className="metrics-list__row">
                        <span>{label}</span>
                        <strong>{formatBytes(value)}</strong>
                      </div>
                      <div className="metrics-list__bar">
                        <span
                          className="metrics-list__bar-fill"
                          style={{ width: `${(value / vpnTrafficMax) * 100}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="metrics-panel__footer">
            <span className="metrics-chip">
              Топ по устройствам: {vpnTopDevices[0]?.display_name || "—"} ({formatNumber(vpnTopDevices[0]?.active_devices || 0)})
            </span>
            <span className="metrics-chip">
              Active window: {formatNumber(vpnAdmin?.windows?.device_active_window_seconds || 0)}с
            </span>
          </div>
        </section>

        <section className="metrics-panel">
          <div className="metrics-panel__header">
            <h2>ITC VPN: недавние события</h2>
            <span>Блокировки, разблокировки и ручные переключения</span>
          </div>
          <div className="metrics-panel__body">
            {vpnRecentEvents.length === 0 ? (
              <p className="metrics-panel__empty">Пока нет событий ITC VPN</p>
            ) : (
              <div className="metrics-list">
                {vpnRecentEvents.slice(0, 10).map((event) => (
                  <div
                    className="metrics-list__item"
                    key={`vpn-event-${event.id}`}
                  >
                    <div className="metrics-list__row">
                      <span>
                        {formatVPNEventLabel(event.event_type)} · {event.display_name}
                      </span>
                      <strong>{formatDateTimeLabel(event.created_at)}</strong>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="metrics-panel__footer">
            {vpnTopDevices.slice(0, 4).map((item) => (
              <span className="metrics-chip" key={`vpn-dev-${item.telegram_id || item.display_name}`}>
                {(item.display_name || item.marzban_username || item.telegram_id)}: {formatNumber(item.active_devices)} IP
              </span>
            ))}
            {!vpnTopDevices.length && <span className="metrics-chip">Нет активных IP</span>}
          </div>
        </section>

        <section className="metrics-panel">
          <div className="metrics-panel__header">
            <h2>Трафик API</h2>
            <span>Топ маршрутов</span>
          </div>
          <div className="metrics-panel__body">
            {summary.topEndpoints.length === 0 ? (
              <p className="metrics-panel__empty">Нет данных по запросам</p>
            ) : (
              <div className="metrics-list">
                {summary.topEndpoints.map((item) => (
                  <div className="metrics-list__item" key={item.path}>
                    <div className="metrics-list__row">
                      <span>{item.path}</span>
                      <strong>{formatNumber(item.value)}</strong>
                    </div>
                    <div className="metrics-list__bar">
                      <span
                        className="metrics-list__bar-fill"
                        style={{
                          width: `${(item.value / maxEndpoint) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="metrics-panel__footer">
            {summary.statusBreakdown.map((item) => (
              <span className="metrics-chip" key={item.status}>
                {item.status}: {formatNumber(item.value)}
              </span>
            ))}
          </div>
        </section>

        <section className="metrics-panel metrics-panel--accent">
          <div className="metrics-panel__header">
            <h2>Активность задач</h2>
            <span>Поведение пользователей на task-страницах</span>
          </div>
          <div className="metrics-panel__body">
            {summary.taskVisitsByPage.length === 0 ? (
              <p className="metrics-panel__empty">
                Нет данных по страницам задач
              </p>
            ) : (
              <div className="metrics-list">
                {summary.taskVisitsByPage.map((item) => (
                  <div className="metrics-list__item" key={item.page}>
                    <div className="metrics-list__row">
                      <span>{item.page}</span>
                      <strong>{formatNumber(item.value)}</strong>
                    </div>
                    <div className="metrics-list__bar">
                      <span
                        className="metrics-list__bar-fill"
                        style={{
                          width: `${(item.value / maxTaskPage) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="metrics-panel__footer">
            <span className="metrics-chip">
              Отчетов: {formatNumber(summary.taskCompletions)}
            </span>
            <span className="metrics-chip">
              Исполнителей: {formatNumber(summary.completionUsers)}
            </span>
            <span className="metrics-chip">
              На проверку: {formatNumber(summary.taskSentForReview)}
            </span>
            <span className="metrics-chip">
              Прямое завершение: {formatNumber(summary.taskCompletedDirect)}
            </span>
          </div>
        </section>

        <section className="metrics-panel">
          <div className="metrics-panel__header">
            <h2>Переходы статусов</h2>
            <span>Какие этапы задач встречаются чаще</span>
          </div>
          <div className="metrics-panel__body">
            {transitionBars.length === 0 ? (
              <p className="metrics-panel__empty">
                Нет данных по переходам статусов
              </p>
            ) : (
              <div className="metrics-list">
                {transitionBars.map((item) => (
                  <div
                    className="metrics-list__item"
                    key={`${item.from}->${item.to}`}
                  >
                    <div className="metrics-list__row">
                      <span>
                        {item.from} {"->"} {item.to}
                      </span>
                      <strong>{formatNumber(item.value)}</strong>
                    </div>
                    <div className="metrics-list__bar">
                      <span
                        className="metrics-list__bar-fill"
                        style={{
                          width: `${(item.value / maxTransition) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="metrics-panel__footer">
            <span className="metrics-chip">
              Создано: {formatNumber(summary.taskCreated)}
            </span>
            <span className="metrics-chip">
              Обновлено: {formatNumber(summary.taskUpdated)}
            </span>
            <span className="metrics-chip">
              Удалено: {formatNumber(summary.taskDeleted)}
            </span>
          </div>
        </section>
      </div>
    </section>
  );
}
